import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, Megaphone, RefreshCw } from "lucide-react";
import { MetaTab } from "@/components/ads/MetaParts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GA, lei, GoogleConnect } from "@/components/ads/GoogleConnect";
import { GoogleOverview, GoogleRecs } from "@/components/ads/GoogleParts";
import { AdsChat } from "@/components/ads/AdsChat";
import { McpAccess } from "@/components/ads/McpAccess";

const B = process.env.REACT_APP_BACKEND_URL;

const Invoices = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const ref = useRef(null);
  const load = () => axios.get(`${GA}/invoices`).then((r) => setD(r.data)).catch(() => setD({ imported: [], last_sync: null }));
  useEffect(() => { load(); }, []);
  const sync = async () => {
    setBusy("sync");
    try { const r = await axios.post(`${GA}/invoices/sync`, {}, { timeout: 200000 }); toast.success(r.data.note ? "Verificat — nimic de importat încă" : `Facturi noi importate: ${r.data.imported.length}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy("");
  };
  const upload = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    setBusy("up");
    try {
      const fd = new FormData(); files.forEach((f) => fd.append("files", f));
      const r = await axios.post(`${B}/api/financiar/facturi/bulk-import-pdf`, fd, { params: { country: "RO" }, timeout: 300000 });
      toast.success(`Importate în Facturi/Financiar: ${r.data.imported ?? 0}, eșuate: ${r.data.failed ?? 0}`);
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    setBusy("");
  };
  return (
    <div className="space-y-3" data-testid="google-invoices">
      <div className="flex items-center gap-3"><h3 className="text-sm font-semibold text-zinc-200">Facturi Google Ads</h3>
        <button data-testid="invoice-sync-btn" onClick={sync} disabled={!!busy} className="ml-auto flex items-center gap-1.5 rounded-full border border-amber-400/60 px-3 py-1 text-xs text-amber-100 hover:bg-amber-400 hover:text-zinc-950">
          {busy === "sync" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Verifică acum</button>
        <button data-testid="invoice-upload-btn" onClick={() => ref.current?.click()} disabled={!!busy} className="flex items-center gap-1.5 rounded-full border border-zinc-700 px-3 py-1 text-xs text-zinc-200 hover:border-amber-400">
          {busy === "up" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Încarcă PDF în Financiar</button>
        <input ref={ref} type="file" accept=".pdf" multiple hidden onChange={upload} data-testid="invoice-upload-input" /></div>
      {d === null ? <Loader2 className="h-4 w-4 animate-spin text-zinc-500" /> : <>
        <p className="text-xs text-zinc-400" data-testid="invoice-auto-note">Automat în fiecare luni la 08:00: facturile noi din Google Ads intră în Financiar ca plătite și la Facturi lunare (cu PDF). Merge doar cu facturare lunară la Google.</p>
        {d.last_sync && <p className={`text-xs ${d.last_sync.note ? "text-amber-300" : "text-emerald-300"}`} data-testid="invoice-last-sync">
          Ultima verificare: {new Date(d.last_sync.at).toLocaleString("ro-RO")} — {d.last_sync.note ? `nimic importat: ${d.last_sync.note}` : `${d.last_sync.found} facturi găsite, ${d.last_sync.imported.length} noi importate`}</p>}
        {d.imported.map((i) => (
          <div key={i.id} className="flex items-center gap-3 rounded-lg border border-zinc-800 px-3 py-2 text-sm text-zinc-200" data-testid={`invoice-${i.id}`}>
            <span className="flex-1">{i.date} · {i.id}</span><span>{lei(i.total)}</span><span className="rounded bg-emerald-500/15 px-2 py-0.5 text-[11px] text-emerald-300">plătită</span>
          </div>))}
      </>}
    </div>
  );
};

const GoogleTab = () => {
  const [st, setSt] = useState(null);
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { axios.get(`${GA}/status`).then((r) => setSt(r.data)).catch((e) => setSt({ connected: false, error: e.message })); }, []);
  useEffect(() => {
    if (!st?.connected) return;
    setD(null); setErr("");
    axios.get(`${GA}/overview`, { params: { days }, timeout: 90000 }).then((r) => setD(r.data)).catch((e) => setErr(e.response?.data?.detail || e.message));
  }, [st?.connected, days]);
  if (!st) return <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />;
  if (!st.connected) return <GoogleConnect status={st} onConnected={setSt} />;
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <span className="text-sm text-zinc-300" data-testid="google-account">{st.account?.descriptiveName} · conectat</span>
        <div className="ml-auto flex gap-1">{[7, 30, 90].map((n) => <button key={n} data-testid={`days-${n}`} onClick={() => setDays(n)} className={`rounded-full px-3 py-1 text-xs ${days === n ? "bg-zinc-100 text-zinc-950" : "text-zinc-400"}`}>{n} zile</button>)}</div>
      </div>
      {err && <p className="text-sm text-rose-300" data-testid="google-error">{err}</p>}
      <AdsChat />
      {!d && !err ? <Loader2 className="h-5 w-5 animate-spin text-zinc-500" /> : d && <GoogleOverview d={d} days={days} />}
      <GoogleRecs days={days} />
      <Invoices />
      <McpAccess />
    </div>
  );
};

export default function Ads() {
  return (
    <div className="space-y-6 p-6" data-testid="ads-page">
      <h1 className="flex items-center gap-2 text-2xl font-semibold text-zinc-100"><Megaphone className="h-6 w-6 text-amber-400" /> Meta & Google Ads</h1>
      <Tabs defaultValue="google">
        <TabsList><TabsTrigger value="google" data-testid="tab-google">Google Ads</TabsTrigger><TabsTrigger value="meta" data-testid="tab-meta">Meta Ads</TabsTrigger></TabsList>
        <TabsContent value="google" className="pt-4"><GoogleTab /></TabsContent>
        <TabsContent value="meta" className="pt-4"><MetaTab /></TabsContent>
      </Tabs>
    </div>
  );
}
